import { useCallback, useState } from 'react';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';

import { queryClient } from '@hotjar/webapp/utils/react-query';
import { useNotify } from '@hotjar/hotsauce';
import { logError } from '@hotjar/webapp/lib/logger';
import { useOwnedAccount } from '@hotjar/account';

import type { OrganizationModel } from '../model';
import { disableSsoOnOrganization, IDENTITY_PROVIDERS_QUERY_KEY } from '../api';

export interface UseIdpDisconnectionFlow {
  disconnectionState: DisconnectionState;
  isLoading: boolean;
  selectedOrganization: OrganizationModel | null;
  selectOrganizationForDisconnection: (organization: OrganizationModel) => void;
  confirmDisconnection: () => void;
  dismissDisconnection: () => void;
}

export enum DisconnectionState {
  INIT = 'init',
  CONFIRM = 'confirm',
  SUCCESS = 'success',
}

export function useIdpDisconnectionFlow(): UseIdpDisconnectionFlow {
  const { data: account } = useOwnedAccount();
  const accountId = account?.id;
  const { t } = useTranslation();
  const { showToast } = useNotify();

  const [selectedOrganization, setSelectedOrganization] = useState<OrganizationModel | null>(null);
  const [disconnectionState, setDisconnectionState] = useState(DisconnectionState.INIT);
  const [isLoading, setIsLoading] = useState(false);

  const selectOrganizationForDisconnection = useCallback((organization: OrganizationModel) => {
    setSelectedOrganization(organization);
    setDisconnectionState(DisconnectionState.CONFIRM);
  }, []);
  const disableSsoMutation = useMutation(disableSsoOnOrganization);

  const confirmDisconnection = useCallback(async () => {
    if (!accountId || !selectedOrganization) {
      return;
    }

    setIsLoading(true);

    try {
      await disableSsoMutation.mutateAsync({
        accountId,
        organizationId: selectedOrganization?.id,
        identityProviderLoginId: null,
      });
      setDisconnectionState(DisconnectionState.SUCCESS);
    } catch (error) {
      // @ts-expect-error fix catch error type
      logError(error);
      showToast({
        kind: 'danger',
        text: t('common:settings.sso.disconnect-modal.error'),
      });
    } finally {
      setIsLoading(false);
    }
  }, [accountId, disableSsoMutation, selectedOrganization, showToast, t]);

  const dismissDisconnection = useCallback(() => {
    const connectionStateBeforeDismissal = disconnectionState;

    if (connectionStateBeforeDismissal === DisconnectionState.SUCCESS) {
      queryClient.invalidateQueries(IDENTITY_PROVIDERS_QUERY_KEY);
    }

    setSelectedOrganization(null);
    setDisconnectionState(DisconnectionState.INIT);
  }, [disconnectionState]);

  return {
    disconnectionState,
    isLoading,
    selectedOrganization,
    selectOrganizationForDisconnection,
    confirmDisconnection,
    dismissDisconnection,
  };
}
